import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { NgxPermissionsService } from 'ngx-permissions';
import { catchError, of, take } from 'rxjs';

import { BillingService } from '../../../../core/services/billing';
import { BillingSummary } from '../../../../models/billing-summary.model';
import { User } from '../../../../models/user.model';
import { WidgetSummaryDisplayComponent } from '../../../../shared/components/widget-displays/widget-summary-display/widget-summary-display.component';
import { Permission } from '../../../../shared/constants/permissions';
import { SharedModule } from '../../../../shared/shared.module';

import * as fromStore from '../../../../store';

@Component({
  selector: 'app-ons-widget-summary',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [WidgetSummaryDisplayComponent, SharedModule],
  template: `
    <app-widget-summary-display
      [loading]="isLoading()"
      [accountName]="$safeNavigationMigration(user()?.Account?.Name)"
      [billingAddress]="userBillingAddress()"
      [currentBalance]="$safeNavigationMigration(summary()?.currentBalance)"
      [isCreditBalance]="$safeNavigationMigration(summary()?.isCreditBalance)"
      [lastPaymentDate]="$safeNavigationMigration(summary()?.lastPayment?.date)"
      [lastPaymentAmount]="
        $safeNavigationMigration(summary()?.lastPayment?.amount)
      "
    ></app-widget-summary-display>
  `,
})
export class WidgetSummaryComponent implements OnInit {
  private readonly store = inject(Store<fromStore.State>);
  private readonly billingService = inject(BillingService);
  private readonly permissionsService = inject(NgxPermissionsService);

  readonly canReadBilling = signal(false);

  readonly isLoading = toSignal<boolean>(
    this.store.select(fromStore.getCurrentUserLoading),
  );

  readonly user = toSignal<User>(this.store.select(fromStore.getCurrentUser));

  readonly summary = signal<BillingSummary | null>(null);

  readonly userBillingAddress = computed<string>(() => {
    const user = this.user();
    if (!user?.Account?.BillingAddress) return '';

    const { street, city, stateCode, postalCode } = user.Account
      .BillingAddress as Record<string, string>;
    const line1 = street ?? '';
    const cityState = [city, stateCode].filter(Boolean).join(', ');
    const line2 = [cityState, postalCode].filter(Boolean).join(' ');
    return [line1, line2].filter(Boolean).join('\r\n');
  });

  async ngOnInit(): Promise<void> {
    const canRead = await this.permissionsService.hasPermission(
      Permission.BILLING_READ,
    );
    this.canReadBilling.set(canRead);

    if (canRead) {
      this.billingService
        .getSummary()
        .pipe(
          take(1),
          catchError(() => of(null)),
        )
        .subscribe((s) => this.summary.set(s));
    }
  }
}
